import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { FormDialog, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import { feedbackCreateSchema, type FeedbackCreateFormValues } from '../schema/feedbackSchema';
import { useSubmitFeedback } from '../hooks/useSubmitFeedback';
import { useMyFeedback } from '../hooks/useMyFeedback';
import { useFeedbackSeen } from '../hooks/useFeedbackSeen';
import type { FeedbackApiError } from '../types/feedback';

interface FeedbackDialogProps {
  open: boolean;
  /** 현재 상세 모듈 코드 자동 프리필 — 일반 문의 진입 시 null */
  moduleCode: string | null;
  /** 열릴 때 활성 탭(queue #691 §R691-2(d) `?feedback=mine` 딥링크) — 기본 'submit'(하위호환) */
  initialTab?: FeedbackTabKey;
  onClose: () => void;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

/** queue #684 §R684-4(b): [문의하기] | [내 문의 이력] 2탭 구조 */
type FeedbackTabKey = 'submit' | 'mine';

const fieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  marginBottom: 'var(--spacing-3)',
};

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-text-secondary)',
  fontWeight: 600,
  display: 'block',
  marginBottom: 'var(--spacing-1)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  background: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-surface-border)',
  borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  boxSizing: 'border-box',
};

const inputErrorStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: 'var(--color-danger)',
};

const errorMsgStyle: React.CSSProperties = {
  fontSize: 'var(--font-size-xs)',
  color: 'var(--color-danger)',
  marginTop: 2,
};

const tabListStyle: React.CSSProperties = {
  display: 'flex',
  gap: 'var(--spacing-2)',
  borderBottom: '1px solid var(--color-surface-border)',
  marginBottom: 'var(--spacing-4)',
};

const tabButtonStyle = (active: boolean): React.CSSProperties => ({
  padding: 'var(--spacing-2) var(--spacing-3)',
  border: 'none',
  borderBottom: active ? '2px solid var(--color-brand-primary)' : '2px solid transparent',
  background: 'transparent',
  color: active ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-sm)',
  fontWeight: active ? 700 : 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family-sans)',
});

/**
 * FeedbackDialog — 모듈 피드백/문의 제출 + 내 문의 이력 다이얼로그.
 * queue #663 §UAT-2026-07-09-R663 §R663-3 (제출 폼) + queue #684 §UAT-2026-07-14-R684 §R684-4(b)
 * (2탭 구조 확장: [문의하기] 기존 제출 폼 무변경 · [내 문의 이력] GET /catalog/feedback/mine 신규).
 * ui-kit FormDialog + react-hook-form + zod. guest/user/admin 전원 제출 가능(인증-any).
 */
export function FeedbackDialog({
  open,
  moduleCode,
  initialTab = 'submit',
  onClose,
  onSuccess,
  onError,
}: FeedbackDialogProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<FeedbackTabKey>(initialTab);
  const submitMutation = useSubmitFeedback();
  const isMineTab = tab === 'mine';
  const mineQuery = useMyFeedback({}, { enabled: open && isMineTab });
  const { markSeen } = useFeedbackSeen();
  // queue #691 §R691-2(4): '내 문의 이력' 탭이 실제로 열려 조회가 성공한 시점(패널 마운트)에
  // 1회만 markSeen() — open/tab 전환마다 리셋(중복 소음 없는 단일 확인 처리).
  const markedSeenRef = useRef(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FeedbackCreateFormValues>({
    resolver: zodResolver(feedbackCreateSchema),
    defaultValues: { kind: 'QUESTION', body: '', moduleCode },
  });

  // 다른 모듈 상세에서 재오픈될 수 있으므로, open 전환 시 현재 moduleCode 로 초기화 +
  // 탭을 initialTab 으로 리셋(기본 'submit' 하위호환 · §R691 'mine' 딥링크).
  useEffect(() => {
    if (open) {
      setTab(initialTab);
      markedSeenRef.current = false;
      reset({ kind: 'QUESTION', body: '', moduleCode });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, moduleCode, initialTab]);

  // queue #691 §R691-2(4): 이력 탭 열람 = 확인 처리(재조회 없이 즉시 배지 소거 ·
  // useFeedbackSeen 의 useSyncExternalStore + 커스텀 이벤트로 동일 탭 동기화).
  useEffect(() => {
    if (isMineTab && mineQuery.isSuccess && !markedSeenRef.current) {
      markedSeenRef.current = true;
      markSeen();
    }
  }, [isMineTab, mineQuery.isSuccess, markSeen]);

  const bodyValue = watch('body') ?? '';
  const isSubmitting = submitMutation.isPending;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose();
  };

  const onSubmit = (values: FeedbackCreateFormValues) => {
    submitMutation.mutate(
      { kind: values.kind, body: values.body, moduleCode: moduleCode ?? undefined },
      {
        onSuccess: () => {
          onSuccess?.(t('showcase.feedback.submit.success'));
          onClose();
        },
        onError: (err) => {
          const apiErr = err as unknown as FeedbackApiError;
          onError?.(apiErr?.message ?? t('showcase.feedback.submit.error'));
        },
      }
    );
  };

  if (!open) return null;

  return (
    <FormDialog
      open={open}
      title={t('showcase.feedback.dialog.title')}
      onClose={handleClose}
      onSubmit={isMineTab ? () => {} : handleSubmit(onSubmit)}
      submitLabel={isSubmitting ? t('showcase.feedback.submitting') : t('showcase.feedback.submit.button')}
      cancelLabel={isMineTab ? t('showcase.feedback.mine.close') : t('showcase.feedback.cancel')}
      submitTestId="feedback-submit-btn"
      hideSubmit={isMineTab}
    >
      <div role="tablist" aria-label={t('showcase.feedback.dialog.title')} style={tabListStyle}>
        <button
          type="button"
          role="tab"
          id="feedback-tab-submit"
          aria-selected={!isMineTab}
          aria-controls="feedback-tabpanel-submit"
          data-testid="feedback-tab-submit"
          onClick={() => setTab('submit')}
          style={tabButtonStyle(!isMineTab)}
        >
          {t('showcase.feedback.tab.submit')}
        </button>
        <button
          type="button"
          role="tab"
          id="feedback-tab-mine"
          aria-selected={isMineTab}
          aria-controls="feedback-tabpanel-mine"
          data-testid="feedback-tab-mine"
          onClick={() => setTab('mine')}
          style={tabButtonStyle(isMineTab)}
        >
          {t('showcase.feedback.tab.mine')}
        </button>
      </div>

      {!isMineTab && (
        <div id="feedback-tabpanel-submit" role="tabpanel" aria-labelledby="feedback-tab-submit">
          {moduleCode && (
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginTop: 0,
                marginBottom: 'var(--spacing-3)',
              }}
            >
              {t('showcase.feedback.moduleCode.label')}{' '}
              <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-brand-primary)' }}>
                {moduleCode}
              </code>
            </p>
          )}

          <div style={fieldStyle}>
            <label htmlFor="feedback-kind" style={labelStyle}>
              {t('showcase.feedback.kind.label')} *
            </label>
            <select
              id="feedback-kind"
              {...register('kind')}
              disabled={isSubmitting}
              aria-invalid={!!errors.kind}
              aria-describedby={errors.kind ? 'feedback-kind-error' : undefined}
              style={errors.kind ? inputErrorStyle : inputStyle}
            >
              <option value="QUESTION">{t('showcase.feedback.kind.question')}</option>
              <option value="BUG">{t('showcase.feedback.kind.bug')}</option>
              <option value="IMPROVEMENT">{t('showcase.feedback.kind.improvement')}</option>
            </select>
            {errors.kind && (
              <span id="feedback-kind-error" role="alert" style={errorMsgStyle}>
                {errors.kind.message}
              </span>
            )}
          </div>

          <div style={fieldStyle}>
            <label htmlFor="feedback-body" style={labelStyle}>
              {t('showcase.feedback.body.label')} *
            </label>
            <textarea
              id="feedback-body"
              rows={5}
              maxLength={4000}
              placeholder={t('showcase.feedback.body.placeholder')}
              {...register('body')}
              disabled={isSubmitting}
              aria-invalid={!!errors.body}
              aria-describedby={errors.body ? 'feedback-body-error' : 'feedback-body-counter'}
              style={{ ...(errors.body ? inputErrorStyle : inputStyle), resize: 'vertical', minHeight: 96 }}
            />
            <span
              id="feedback-body-counter"
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2, alignSelf: 'flex-end' }}
            >
              {bodyValue.length}/4000
            </span>
            {errors.body && (
              <span id="feedback-body-error" role="alert" style={errorMsgStyle}>
                {errors.body.message}
              </span>
            )}
          </div>
        </div>
      )}

      {isMineTab && (
        <div id="feedback-tabpanel-mine" role="tabpanel" aria-labelledby="feedback-tab-mine">
          {mineQuery.isLoading && (
            <div
              role="status"
              aria-busy="true"
              aria-label={t('showcase.feedback.admin.loading')}
              data-testid="feedback-mine-loading"
            >
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    height: 14,
                    marginBottom: 'var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    width: `${60 + ((i * 10) % 30)}%`,
                  }}
                />
              ))}
              <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }`}</style>
            </div>
          )}

          {mineQuery.isError && (
            <ErrorState
              title={t('showcase.feedback.admin.error.title')}
              message={t('showcase.feedback.admin.error.message')}
              onRetry={() => void mineQuery.refetch()}
            />
          )}

          {!mineQuery.isLoading && !mineQuery.isError && (mineQuery.data?.content.length ?? 0) === 0 && (
            <EmptyState title={t('showcase.feedback.mine.empty')} />
          )}

          {!mineQuery.isLoading && !mineQuery.isError && (mineQuery.data?.content.length ?? 0) > 0 && (
            <ul
              data-testid="feedback-mine-list"
              style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-3)',
                maxHeight: 360,
                overflowY: 'auto',
              }}
            >
              {mineQuery.data!.content.map((item) => (
                <li
                  key={item.id}
                  data-testid={`feedback-mine-item-${item.id}`}
                  style={{
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--spacing-3)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', fontWeight: 600 }}>
                      {t(`showcase.feedback.kind.${item.kind.toLowerCase()}`)}
                    </span>
                    <StatusPill tone={item.status === 'ANSWERED' ? 'success' : 'warning'}>
                      {t(item.status === 'ANSWERED' ? 'showcase.feedback.status.answered' : 'showcase.feedback.status.received')}
                    </StatusPill>
                  </div>
                  <p style={{ margin: 'var(--spacing-2) 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)' }}>
                    {item.body}
                  </p>
                  {item.status === 'ANSWERED' && item.answer ? (
                    <div
                      data-testid="feedback-mine-answer"
                      style={{
                        marginTop: 'var(--spacing-2)',
                        paddingTop: 'var(--spacing-2)',
                        borderTop: '1px dashed var(--color-surface-border)',
                      }}
                    >
                      <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)' }}>
                        {t('showcase.feedback.mine.answerLabel')}
                      </span>
                      <p style={{ margin: 'var(--spacing-1) 0 0', fontSize: 'var(--font-size-sm)' }}>{item.answer}</p>
                      {item.answeredAt && (
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                          {new Date(item.answeredAt).toLocaleString()}
                        </span>
                      )}
                    </div>
                  ) : (
                    <p
                      data-testid="feedback-mine-waiting"
                      style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}
                    >
                      {t('showcase.feedback.mine.waiting')}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </FormDialog>
  );
}
